<template>
  <b-container class="d-flex flex-column h-100" fluid>
    <div
      class="pagetitle-container d-flex justify-content-between align-items-center"
    >
      <div class="page-title">
        <h2>
          Projekty <small>({{ count }})</small>
        </h2>
      </div>
      <div class="d-flex flex-grow-1">
        <input-filter
          ref="filter"
          :filters="table.filter"
          :options="filterOptions"
          @onReset="resetFilter"
          @onSearch="search"
        >
          <template #filter>
            <b-row>
              <b-col md="4">
                <b-form-group
                  label="Komodita:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="type"
                >
                  <b-input-group class="input-group-labeled">
                    <selectbox
                      v-model="table.filter.type"
                      :options="[
                        { id: 1, name: 'Elektrina' },
                        { id: 2, name: 'Plyn' },
                        { id: 3, name: 'Voda' },
                        { id: 4, name: 'Teplo' },
                      ]"
                      :searchable="false"
                      multiple
                      @input="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.type = []
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group
                  label="Oblasť:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="area"
                >
                  <b-input-group class="input-group-labeled">
                    <selectbox
                      v-model="table.filter.area"
                      :options="[
                        { id: 1, name: 'Energ. manažment' },
                        { id: 2, name: 'Inžiniering' },
                      ]"
                      :searchable="false"
                      @input="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.area = null
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col md="4">
                <b-form-group
                  label="Stav:"
                  label-align-md="right"
                  label-cols-md="4"
                  label-for="status"
                >
                  <b-input-group class="input-group-labeled">
                    <selectbox
                      v-model="table.filter.status"
                      :options="[
                        { id: 1, name: 'Nový' },
                        { id: 2, name: 'Ukončený' },
                      ]"
                      :searchable="false"
                      @input="search"
                    />
                    <b-input-group-append>
                      <b-btn
                        size="sm"
                        variant="link"
                        @click="
                          table.filter.status = null
                          search()
                        "
                      >
                        <b-icon icon="x" scale="1.5x" />
                      </b-btn>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-col>
            </b-row>
          </template>
        </input-filter>
      </div>
    </div>
    <div ref="scroll" v-bar class="bar-out">
      <div>
        <div
          v-if="$root.user.isAdmin"
          class="subpagetitle-container d-flex justify-content-between align-items-center mb-3"
        >
          <b-btn id="newFolderPop" variant="primary">
            <icon class="mr-1" name="folder" />
            Vytvoriť projekt
          </b-btn>
          <b-popover
            target="newFolderPop"
            triggers="focus"
            @shown="focusProjectInput"
          >
            <b-form-input
              ref="projectInput"
              v-model="project.name"
              v-validate="'required'"
              :class="{ 'is-invalid': errors.has('project.name') }"
              name="foldername"
              placeholder="Názov projektu"
              @keydown.enter.native="createProject"
            />
            <b-form-invalid-feedback>
              {{ errors.first('project.name') }}
            </b-form-invalid-feedback>
          </b-popover>
        </div>
        <server-table
          ref="table"
          :api-request="apiRequest"
          :columns="table.columns"
          :searchable="false"
          :sort-by.sync="table.sortBy"
          bordered
          hover
          striped
          @refreshed="onLoaded"
        >
          <template v-if="$root.user.isAdmin" #cell(actions)="data">
            <b-dropdown class="btn-p-0" no-caret variant="link">
              <template slot="button-content">
                <b-icon icon="text-indent-left" scale="1.25x" />
              </template>
              <b-dropdown-item
                @click.prevent="openDetailModal(data.item.id, data.item)"
              >
                <icon class="icon-fw mr-2" name="info" scale="0.75" />
                Informácie
              </b-dropdown-item>
              <b-dropdown-item
                @click.prevent="renameF(data.item.id, data.item.name)"
              >
                <icon class="icon-fw mr-2" name="edit" scale="0.75" />
                Premenovať
              </b-dropdown-item>
              <b-dropdown-item
                @click.prevent="openNoteModal(data.item.id, data.item.note)"
              >
                <icon class="icon-fw mr-2" name="pencil-alt" scale="0.75" />
                Poznámky
              </b-dropdown-item>
              <b-dropdown-item
                @click.prevent="openShareModal(data.item.id, data.item.user_id)"
              >
                <icon class="icon-fw mr-2" name="share-alt" scale="0.75" />
                Zdieľanie
              </b-dropdown-item>
              <b-dropdown-item
                class="text-danger"
                @click.prevent="removeProject(data.item.id)"
              >
                <b-icon icon="trash" class="mr-1" />
                Vymazať
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template #cell(name)="data">
            <template v-if="!rename || rename !== data.item.id">
              <router-link
                :to="{ name: 'projectsView', params: { id: data.item.id } }"
                class="text-body"
              >
                <img class="mr-2" src="/images/folder.png" />
                <strong>{{ data.value }}</strong>
                <sup class="ml-1">
                  <span
                    v-if="
                      $root.user.isAdmin &&
                      data.item.user_id &&
                      data.item.user_id.length
                    "
                    class="text-primary font-weight-bold"
                  >
                    <b-icon icon="person-plus-fill" />
                    {{ data.item.user_id.length }}
                  </span>
                </sup>
              </router-link>
              <div
                v-if="data.item.note"
                class="fs-sm text-muted px-3 py-2 mt-1"
                v-html="$options.filters.nl2br(data.item.note)"
              />
            </template>
            <b-form-input
              v-if="rename === data.item.id"
              v-model="renameValue"
              v-validate="'required'"
              :class="{ 'is-invalid': errors.has('renameValue') }"
              name="rename"
              @keydown.enter.native="renameProject(data.item.id)"
            />
          </template>
          <template #cell(type)="data">
            <template v-if="data.item._type">
              <template v-for="type in data.item._type">
                <span
                  v-if="type.const === 'POWER'"
                  class="badge bg-power align-top fs-sm"
                >
                  <icon class="icon-fw" name="plug" scale="0.675" />
                  {{ type.name }}
                </span>
                <span
                  v-if="type.const === 'GAS'"
                  class="badge bg-gas align-top fs-sm"
                >
                  <icon class="icon-fw" name="fire" scale="0.675" />
                  {{ type.name }}
                </span>
                <span
                  v-if="type.const === 'WATER'"
                  class="badge bg-water align-top fs-sm"
                >
                  <icon class="icon-fw" name="tint" scale="0.675" />
                  {{ type.name }}
                </span>
                <span
                  v-if="type.const === 'HEAT'"
                  class="badge bg-heat align-top fs-sm"
                >
                  <icon class="icon-fw" name="sun" scale="0.675" />
                  {{ type.name }}
                </span>
              </template>
            </template>
            <template v-else> - </template>
          </template>
          <template #cell(status)="data">
            <template v-if="data.item._status">
              <span
                v-if="data.item._status.const === 'NEW'"
                class="badge badge-success align-top fs-base font-weight-normal"
                ><b-icon icon="check2-circle" />
                {{ data.item._status.name }}</span
              >
              <span
                v-if="data.item._status.const === 'CLOSED'"
                class="badge badge-danger align-top fs-base font-weight-normal"
                ><b-icon icon="x" /> {{ data.item._status.name }}</span
              >
            </template>
            <template v-else> - </template>
          </template>
        </server-table>
      </div>
    </div>
    <modal
      ref="addDetail"
      name="projects-add-detail"
      cancel-title="Zrušiť"
      ok-title="Uložiť"
      size="sm"
      title="Informácie"
      @hidden="
        details = {}
        project_id = null
      "
      @ok="editDetails"
    >
      <b-form-group
        v-if="detailsOptions.type"
        label="Typ energie"
        label-for="typeID"
      >
        <selectbox
          v-model="details.typeID"
          :options="detailsOptions.type"
          multiple
          placeholder="Typ energie"
        >
          <template slot="noResult"> Typ nenájdený </template>
        </selectbox>
      </b-form-group>
      <b-form-group
        v-if="detailsOptions.area"
        label="Oblasť"
        label-for="areaID"
      >
        <selectbox
          v-model="details.areaID"
          :options="detailsOptions.area"
          placeholder="Oblasť"
        >
          <template slot="noResult"> Oblasť nenájdená </template>
        </selectbox>
      </b-form-group>
      <b-form-group
        v-if="detailsOptions.status"
        label="Stav"
        label-for="statusID"
      >
        <selectbox
          v-model="details.statusID"
          :options="detailsOptions.status"
          placeholder="Stav"
        >
          <template slot="noResult"> Stav nenájdený </template>
        </selectbox>
      </b-form-group>
      <b-form-group label="Dátum ukončenia" label-for="endDate">
        <single-date
          v-model="details.endDate"
          class="form-control"
          name="endDate"
        />
      </b-form-group>
    </modal>
    <modal
      ref="addNote"
      name="projects-add-note"
      cancel-title="Zrušiť"
      ok-title="Uložiť"
      size="sm"
      title="Poznámky"
      @hidden="
        note = ''
        project_id = null
      "
      @ok="editNote"
    >
      <textarea
        v-if="$root.user.isAdmin"
        v-model="note"
        class="form-control"
        placeholder="žiadne poznámky (kliknutím pridajte)"
        rows="4"
        wrap="soft"
      />
    </modal>
    <modal
      ref="addShare"
      name="projects-add-share"
      cancel-title="Zrušiť"
      ok-title="Uložiť"
      size="sm"
      title="Nastavenie zdieľania"
      @hidden="((project_id = null), (user_id = []))"
      @ok="editShare"
    >
      <selectbox
        v-model="user_id"
        :custom-label="userLabel"
        :options="users"
        multiple
        searchable
        placeholder="Vyberte užívateľa"
      >
        <template slot="noResult"> Užívateľ nenájdený </template>
      </selectbox>
    </modal>
  </b-container>
</template>
<script>
import InputFilter from '@/components/InputFilter'
import Selectbox from '@/components/inputs/Selectbox'
import SingleDate from '@/components/inputs/SingleDate'
import ServerTable from '@/components/ServerTable'
import Modal from '@/components/Modal'

export default {
  components: {
    InputFilter,
    Selectbox,
    SingleDate,
    ServerTable,
    Modal,
  },
  data() {
    return {
      loading: false,
      table: {
        columns: [
          {
            key: 'actions',
            label: '',
            class: 'th-shrink',
          },
          {
            key: 'id',
            label: 'ID',
            class: 'th-shrink text-right',
            sortable: true,
          },
          {
            key: 'name',
            label: 'Názov',
            tdClass: 'td-link',
            sortable: true,
            sortableKey: 'projects.name',
            searchable: true,
          },
          {
            key: 'type',
            label: 'Typ',
          },
          {
            key: 'area',
            label: 'Oblasť',
            formatter: (value, key, item) =>
              item._area ? item._area.name : '-',
            sortable: true,
            sortableKey: 'areaID',
          },
          {
            key: 'status',
            label: 'Stav',
            sortable: true,
            sortableKey: 'statusID',
          },
          {
            key: 'created_at',
            label: 'Dátum pridania',
            formatter: (value) =>
              value ? this.$options.filters.formatDate(value) : '-',
            sortable: true,
          },
          {
            key: 'endDate',
            label: 'Dátum ukončenia',
            formatter: (value) =>
              value ? this.$options.filters.formatDate(value) : '-',
            sortable: true,
          },
          {
            key: 'created_by',
            label: 'Vytvoril',
            formatter: (value, key, item) =>
              item.lastname
                ? this.$root.user.isAdmin
                  ? item.firstname + ' ' + item.lastname
                  : 'ESM'
                : '-',
            sortable: true,
          },
        ],
        sortBy: 'name',
        filter: {
          type: [],
          area: null,
          status: null,
        },
      },
      filterOptions: {
        placeholder: 'Filtrovanie a hľadanie...',
      },
      count: null,
      project: {
        name: '',
      },
      detailsOptions: {},
      details: {},
      rename: false,
      renameValue: '',
      users: [],
      user_id: [],
      note: '',
      project_id: null,
    }
  },
  methods: {
    apiRequest(params) {
      return this.$api.get('projects/get_list', {
        params: { filter: this.table.filter, ...params },
      })
    },
    onLoaded() {
      this.count = this.$refs.table.totalRows
    },
    search() {
      this.$refs.table.searchVal = this.$refs.filter.value
      this.$nextTick(() => {
        this.$refs.table.search()
      })
    },
    resetFilter() {
      this.$refs.table.reset()
    },
    focusProjectInput() {
      this.$refs.projectInput.focus()
    },
    createProject() {
      if (this.project.name !== '') {
        this.$api.post('projects/create', this.project).then(() => {
          this.$refs.table.reset()
          this.project.name = ''
        })
      }
    },
    renameF(id, name) {
      this.rename = id
      this.renameValue = name
    },
    renameProject(id) {
      this.$api
        .post('projects/rename', {
          id: id,
          name: this.renameValue,
        })
        .then(() => {
          this.rename = false
          this.renameValue = ''
          this.$refs.table.refresh()
        })
    },
    removeProject(id) {
      this.$swal({
        title: 'Ste si istý?',
        text: 'Naozaj chcete odstrániť tento projekt?',
        type: 'info',
        position: 'top',
        showConfirmButton: true,
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.$api.post(`projects/${id}/remove`).then((res) => {
            this.$swal({
              type: 'success',
              position: 'top',
              showCloseButton: true,
              text: res.data.message,
              timer: 5000,
            })
            this.$refs.table.refresh()
          })
        }
      })
    },
    openDetailModal(id, fields) {
      this.$root.$emit('modal-show', 'projects-add-detail')
      this.project_id = id
      this.details = fields
      this.$api
        .get('attributes/get', {
          params: {
            select: 'projects',
          },
        })
        .then((res) => {
          this.detailsOptions = res.data
        })
    },
    openNoteModal(id, note) {
      this.$root.$emit('modal-show', 'projects-add-note')
      this.project_id = id
      this.note = note
    },
    openShareModal(id, users) {
      this.$root.$emit('modal-show', 'projects-add-share')
      this.project_id = id
      this.user_id = users
      this.$api.get('users/get').then((response) => {
        this.users = response.data
      })
    },
    editDetails() {
      this.$api
        .post('projects/save_details', {
          id: this.project_id,
          typeID: this.details.typeID,
          areaID: this.details.areaID,
          statusID: this.details.statusID,
          endDate: this.details.endDate,
        })
        .then(() => {
          this.$refs.table.refresh()
        })
    },
    editNote() {
      this.$api
        .post('projects/save_project_note', {
          id: this.project_id,
          note: this.note,
        })
        .then(() => {
          this.$refs.table.refresh()
        })
    },
    editShare() {
      this.$api
        .post('projects/save_share', {
          id: this.project_id,
          user_id: this.user_id,
        })
        .then((res) => {
          this.$swal({
            type: 'success',
            position: 'top',
            showCloseButton: true,
            text: res.data.success,
            timer: 5000,
          })
          this.$refs.table.refresh()
        })
    },
    userLabel({ id }) {
      let user = this.users.find((t) => t.id == id)
      return `${user.firstname} ${user.lastname}`
    },
  },
}
</script>
